/**
 * JSON Schema generation from TJS TypeDescriptors and example values
 *
 * Converts TJS type information into standard JSON Schema (draft 2020-12).
 */

import type { TypeDescriptor } from './types'

export interface JSONSchemaObject {
  type?: string | string[]
  properties?: Record<string, JSONSchemaObject>
  items?: JSONSchemaObject | JSONSchemaObject[]
  required?: string[]
  additionalProperties?: boolean
  anyOf?: JSONSchemaObject[]
  minimum?: number
  examples?: unknown[]
  description?: string
  [key: string]: unknown
}

/**
 * Convert a TypeDescriptor to JSON Schema
 */
export function typeDescriptorToJSONSchema(
  td: TypeDescriptor
): JSONSchemaObject {
  if (td.nullable) {
    const base = typeDescriptorToJSONSchema({ ...td, nullable: false })
    return { anyOf: [base, { type: 'null' }] }
  }

  switch (td.kind) {
    case 'string':
      // A regexp type example lands on JSON Schema's NATIVE `pattern` keyword — no
      // `$predicate` extension needed, so even a naive validator enforces it.
      // Flags have no JSON-Schema equivalent; when any are set we emit the looser
      // `{type:'string'}` rather than a pattern that would be wrong (e.g. `/…/i`
      // applied case-SENSITIVELY). Permissive-when-naive is the same progressive
      // -enhancement rule the `$predicate` work follows.
      return td.pattern && !td.flags
        ? { type: 'string', pattern: td.pattern }
        : { type: 'string' }
    case 'number':
      return { type: 'number' }
    case 'integer':
      return { type: 'integer' }
    case 'non-negative-integer':
      return { type: 'integer', minimum: 0 }
    case 'bigint':
      // JSON has no bigint, and `JSON.stringify(1n)` throws. `integer` is the honest
      // description of the VALUE domain (arbitrary precision, no bound — which is
      // exactly what unbounded `integer` means in JSON Schema); how a bigint crosses
      // the wire is a transport question this schema deliberately does not answer.
      return { type: 'integer' }
    case 'boolean':
      return { type: 'boolean' }
    case 'null':
      return { type: 'null' }
    case 'undefined':
      return {}
    case 'any':
      return {}
    case 'array':
      if (td.items) {
        return { type: 'array', items: typeDescriptorToJSONSchema(td.items) }
      }
      return { type: 'array' }
    case 'object':
      if (td.shape) {
        const properties: Record<string, JSONSchemaObject> = {}
        const required: string[] = []
        for (const [key, fieldTd] of Object.entries(td.shape)) {
          properties[key] = typeDescriptorToJSONSchema(fieldTd)
          required.push(key)
        }
        return {
          type: 'object',
          properties,
          required,
          additionalProperties: false,
        }
      }
      return { type: 'object' }
    case 'union':
      if (td.members) {
        return { anyOf: td.members.map(typeDescriptorToJSONSchema) }
      }
      return {}
    // A closed set of literals is exactly JSON Schema's `enum`, so this is lossless too.
    // It had no case at all and fell through to `{}` — accept-anything — which is the
    // worst possible rendering of the one type that knows precisely what it accepts.
    case 'literal-union':
      if (td.values && td.values.length > 0) {
        return { enum: [...td.values] }
      }
      return {}
    default:
      return {}
  }
}

/**
 * Infer a JSON Schema from an example value (without going through TypeDescriptor)
 */
export function exampleToJSONSchema(value: unknown): JSONSchemaObject {
  if (value === null) return { type: 'null' }
  if (value === undefined) return {}

  switch (typeof value) {
    case 'string':
      return { type: 'string' }
    case 'number':
      return Number.isInteger(value) ? { type: 'integer' } : { type: 'number' }
    case 'boolean':
      return { type: 'boolean' }
    case 'object': {
      if (Array.isArray(value)) {
        if (value.length === 0) return { type: 'array' }
        // Infer item type from first element
        return { type: 'array', items: exampleToJSONSchema(value[0]) }
      }
      // Object
      const properties: Record<string, JSONSchemaObject> = {}
      const required: string[] = []
      for (const [key, val] of Object.entries(
        value as Record<string, unknown>
      )) {
        properties[key] = exampleToJSONSchema(val)
        required.push(key)
      }
      return {
        type: 'object',
        properties,
        required,
        additionalProperties: false,
      }
    }
    default:
      return {}
  }
}

/**
 * Generate JSON Schema for function parameters (as an object schema)
 * and return type from __tjs metadata
 */
export function functionMetaToJSONSchema(meta: {
  params: Record<string, any>
  returns?: { type: any; example?: any }
}): { input: JSONSchemaObject; output?: JSONSchemaObject } {
  const properties: Record<string, JSONSchemaObject> = {}
  const required: string[] = []

  for (const [name, paramInfo] of Object.entries(meta.params)) {
    if (paramInfo?.type?.kind) {
      // Has TypeDescriptor
      properties[name] = typeDescriptorToJSONSchema(paramInfo.type)
    } else if (paramInfo?.example !== undefined) {
      // Has example value
      properties[name] = exampleToJSONSchema(paramInfo.example)
    } else {
      properties[name] = {}
    }
    if (paramInfo?.required !== false) {
      required.push(name)
    }
    if (paramInfo?.example !== undefined) {
      properties[name].examples = [paramInfo.example]
    }
  }

  const input: JSONSchemaObject = {
    type: 'object',
    properties,
    required,
  }

  let output: JSONSchemaObject | undefined
  if (meta.returns) {
    if (meta.returns.type?.kind) {
      output = typeDescriptorToJSONSchema(meta.returns.type)
    } else if (meta.returns.example !== undefined) {
      output = exampleToJSONSchema(meta.returns.example)
    }
  }

  return { input, output }
}
